import React, { useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import {
  Checkbox,
  InputHelperText,
  TruncateText,
} from '@mint-lib/tw-components';
import {
  ComboboxInput,
  ComboboxInputLabel,
  ComboboxInputRoot,
  ComboboxItem,
  ComboboxMenu,
} from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';

import { Loader } from './Loader.jsx';
import { VirtualizedYContainer } from './VirtualizedYContainer.jsx';

type MultiSelectComboboxProps<Option> = {
  label?: string;
  placeholder?: string;
  inputValue: string | null;
  selectedValues: Option[];
  options: Option[];
  getOptionValue: (option: Option) => string;
  getOptionLabel: (option: Option) => string;
  getOptionDisabled?: (
    option: Option,
    optionState: { isSelected: boolean },
  ) => boolean;
  renderOption?: (
    option: Option,
    state: { isSelected: boolean; isDisabled: boolean },
  ) => React.ReactNode;
  filterOptions?: (options: Option[], searchValue: string) => Option[];
  onChange: (values: Option[]) => void;
  isLoading?: boolean;
  withSearch?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium';
  helperText?: string;
  noOptionsText?: string;
  className?: string;
  menuClassname?: string;
  menuItemClassName?: string;
};

export const MultiSelectCombobox = <T,>({
  label,
  placeholder,
  inputValue,
  selectedValues,
  options,
  getOptionLabel,
  getOptionValue,
  onChange,
  isLoading,
  withSearch = true,
  disabled,
  size,
  getOptionDisabled,
  renderOption,
  helperText,
  noOptionsText,
  filterOptions,
  className,
  menuClassname,
  menuItemClassName,
}: MultiSelectComboboxProps<T>) => {
  const { t } = useTranslation('@myn/import-hub');
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchValue, setSearchValue] = useState('');

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchValue('');
    }
  };

  const handleSelect = (value: string) => {
    const option = options.find((opt) => getOptionValue(opt) === value);
    if (!option) return;

    const isSelected = selectedValues.some(
      (val) => getOptionValue(val) === value,
    );

    if (isSelected) {
      onChange(selectedValues.filter((val) => getOptionValue(val) !== value));
    } else {
      onChange([...selectedValues, option]);
    }
  };

  const filteredOptions = filterOptions
    ? filterOptions(options, searchValue)
    : options.filter((option) =>
        getOptionLabel(option)
          .toLowerCase()
          .includes(searchValue.toLowerCase()),
      );

  return (
    <ComboboxInputRoot className={className}>
      {label && <ComboboxInputLabel label={label} htmlFor={label} />}
      <ComboboxInput
        placeholder={placeholder}
        value={inputValue ?? ''}
        open={isOpen}
        onOpenChange={handleOpenChange}
        disabled={disabled}
        size={size}
        popoverClassName="z-popover"
      >
        <ComboboxMenu
          shouldFilter={false}
          withSearch={withSearch}
          onValueChange={setSearchValue}
          emptyMessage={
            isLoading ? <Loader /> : (noOptionsText ?? t('No options'))
          }
        >
          <VirtualizedYContainer
            className={cn(
              'max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto',
              menuClassname,
            )}
          >
            {filteredOptions.map((option) => {
              const optionValue = getOptionValue(option);
              const isSelected = selectedValues.some(
                (selectedOption) =>
                  getOptionValue(selectedOption) === optionValue,
              );
              const isDisabled =
                getOptionDisabled?.(option, { isSelected }) || false;

              return (
                <ComboboxItem
                  key={optionValue}
                  value={optionValue}
                  onSelect={handleSelect}
                  disabled={isDisabled}
                  className={menuItemClassName}
                >
                  {renderOption ? (
                    renderOption(option, { isSelected, isDisabled })
                  ) : (
                    <div className="flex min-w-0 items-center gap-1">
                      <Checkbox
                        checked={isSelected}
                        disabled={isDisabled}
                        className={!isDisabled ? 'cursor-pointer' : undefined}
                      />
                      <TruncateText>
                        <div>{getOptionLabel(option)}</div>
                      </TruncateText>
                    </div>
                  )}
                </ComboboxItem>
              );
            })}
          </VirtualizedYContainer>
        </ComboboxMenu>
      </ComboboxInput>
      {helperText && <InputHelperText helperText={helperText} />}
    </ComboboxInputRoot>
  );
};
